/**
 * MISSION: Single, safe entry point for firing PostHog events.
 * CONTEXT_ANCHORS: events.ts (SSOT of names), lib/posthog.ts (window.posthog bridge).
 *
 * Why this exists: scattered `window.posthog?.capture(...)` calls are easy to get
 * wrong (missing guard, fragile dynamic import, an uncaught throw on the boot path).
 * `track()` centralizes the guard and guarantees a capture NEVER breaks the app.
 *
 * Use for NEW or boot-critical events. Existing working captures are left untouched
 * on purpose — migrating them would risk regressing events that already feed the
 * Health Page.
 */

import type { AnalyticsEventName } from './events';

type TrackProps = Readonly<Record<string, unknown>>;

interface PostHogBridge {
    readonly capture: (event: string, props?: TrackProps) => void;
}

/**
 * Fire a PostHog event. Best-effort: if PostHog never loaded (adblocker, network,
 * or the bridge isn't ready yet), this is a silent no-op. It never throws — analytics
 * must never be able to crash a user flow, least of all the boot path.
 *
 * posthog-js queues captures internally and persists them across reloads, so an event
 * fired before the remote lib finishes loading is still delivered.
 */
export function track(event: AnalyticsEventName, props?: TrackProps): void {
    try {
        const ph = (window as unknown as { posthog?: PostHogBridge }).posthog;
        ph?.capture(event, props);
    } catch {
        // Best-effort analytics — swallow to protect the calling flow.
    }
}
